import React from "react";
import { Box, Text, Flex, Divider, Button, HStack } from "@chakra-ui/react";
import { successNotifier } from "../../../components/NotificationHandler";
import { FaFire } from "react-icons/fa";
import { IoWaterOutline } from "react-icons/io5";
import { BsLightbulb } from "react-icons/bs";
import { CreditCard } from "./CreditCard";
import { RateCard } from "./RateCard";
import { Analytics } from "./Analytics";
import { PaymentRecordTable } from "../../../components/PaymentRecordTable";

export const COMMODITY_SECTION_CONFIG = {
  water: { label: "Water" },
  electricity: { label: "Electricity" },
  gas: { label: "Gas" },
};

const QUICK_BUY_COLORS = {
  water: {
    bg: "teal.50",
    scheme: "teal",
    icon: <IoWaterOutline color="#2a9d8f" />,
  },
  electricity: {
    bg: "yellow.50",
    scheme: "yellow",
    icon: <BsLightbulb color="#f6c21a" />,
  },
  gas: { bg: "orange.50", scheme: "orange", icon: <FaFire color="#ff6a00" /> },
};

const SECTION_THEME = {
  water: { bg: "#e8f7f5", headingColor: "teal.700", dividerColor: "teal.100" },
  electricity: {
    bg: "#fefce8",
    headingColor: "yellow.700",
    dividerColor: "yellow.100",
  },
  gas: {
    bg: "#fff4ed",
    headingColor: "orange.700",
    dividerColor: "orange.100",
  },
};

export const CommoditySection = ({
  commodity,
  sectionData,
  paymentRecord,
  sectionRef,
  setDisplayPage,
  setRepurchaseAmount,
  userProfile,
  estateId,
}) => {
  const section = COMMODITY_SECTION_CONFIG[commodity] || { label: commodity };
  const quick = QUICK_BUY_COLORS[commodity] || QUICK_BUY_COLORS.water;
  const theme = SECTION_THEME[commodity] || SECTION_THEME.water;

  const latestPurchaseDate = paymentRecord?.[0]?.createdAt
    ? new Date(paymentRecord[0].createdAt).toLocaleString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <Box
      mb="32px"
      ref={sectionRef}
      bg={theme.bg}
      borderRadius="16px"
      p={["14px", "16px", "20px"]}
    >
      <Text
        fontWeight="bold"
        px={["10px", "10px", "0px"]}
        mb="14px"
        color={theme.headingColor}
        letterSpacing="0.3px"
      >
        {section.label} Section
      </Text>

      {/* ── Top row: CreditCard · RateCard · Quick-buy ─────────────────── */}
      <Flex
        direction={["column", "column", "row"]}
        px={["10px", "10px", "0px"]}
      >
        <Box w={["100%", "100%", "33%"]} mr={["0px", "0px", "20px"]}>
          <CreditCard
            userProfile={userProfile}
            name={sectionData?.creditCard?.name}
            greeting={sectionData?.creditCard?.greeting}
            meterNumber={sectionData?.creditCard?.meterNumber}
            latestPurchaseDate={latestPurchaseDate}
          />
        </Box>

        <Box w={["100%", "100%", "40%"]} mr={["0", "0", "20px"]}>
          <RateCard rateData={sectionData.rate} utilityType={commodity} />
        </Box>

        <Box
          w={["100%", "100%", "27%"]}
          mb={["10px", "10px", "0px"]}
          mt={["20px", "10px", "0px"]}
        >
          <Box
            borderRadius="12px"
            p={4}
            h="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            boxShadow="sm"
            flexWrap="wrap"
            bg={quick.bg}
          >
            <HStack spacing={3} alignItems="center">
              <Box fontSize="22px">{quick.icon}</Box>
              <Box>
                <Text fontWeight="700">Buy {section.label}</Text>
                <Text fontSize="12px" color="gray.600">
                  Fast top-up for your {section.label.toLowerCase()}
                </Text>
              </Box>
            </HStack>

            <Button
              colorScheme={quick.scheme}
              onClick={() => {
                if (commodity !== "water") {
                  successNotifier(`${section.label} buying coming soon`);
                  return;
                }
                setDisplayPage("orderPage");
              }}
              leftIcon={quick.icon}
            >
              Buy {section.label}
            </Button>
          </Box>
        </Box>
      </Flex>

      <Divider
        orientation="horizontal"
        borderColor={theme.dividerColor}
        my="20px"
        display={["block", "block", "none"]}
      />

      {/* ── Charts ─────────────────────────────────────────────────────── */}
      <Box mt="20px">
        <Analytics
          lineChartData={sectionData.lineChart}
          expensesData={sectionData.expenses}
        />
      </Box>

      <Divider
        orientation="horizontal"
        borderColor={theme.dividerColor}
        mt="20px"
        display={["block", "block", "none"]}
      />

      {/* ── Payments ───────────────────────────────────────────────────── */}
      <Box>
        <Text
          mt="20px"
          fontWeight="bold"
          px={["10px", "10px", "0px"]}
          color={theme.headingColor}
        >
          Payment
        </Text>
        <PaymentRecordTable
          userId={userProfile?._id}
          estateId={estateId}
          commodityType={commodity.toUpperCase()}
          onRepurchase={(record) => {
            if (setRepurchaseAmount)
              setRepurchaseAmount(record?.amountPaid || null);
            if (commodity !== "water") {
              successNotifier(`${section.label} commodity coming soon`);
              return;
            }
            setDisplayPage("orderPage");
          }}
        />
      </Box>
    </Box>
  );
};
